import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiErrorCode,
  PERMISSIONS,
  PurchaseInvoiceStatus,
  type PurchaseInvoiceDetail,
  type PurchaseInvoiceLineSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '@/services/apiClient';
import {
  usePurchaseInvoiceQuery,
  useVoidPurchaseInvoiceMutation,
} from '../api/purchase-invoices.api';
import { PurchaseInvoiceStatusBadge } from '../components/PurchaseInvoiceStatusBadge';

/** The SPAY references a refusal named, so the dialog can say exactly what to void first. */
function settlingReferences(error: unknown): string[] {
  if (apiErrorCode(error) !== ApiErrorCode.PURCHASE_INVOICE_HAS_PAYMENTS) return [];
  return apiErrorDetails(error)
    .map((detail) => detail.message.replace(/\s+is posted$/u, '').trim())
    .filter(Boolean);
}

function Detail({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One supplier invoice: what is owed, for which deliveries, and what can still be done about it.
 *
 * Read-only by design. An invoice is posted the moment it is entered and never edited — a mistake
 * is voided and entered again, which leaves both documents in the history where an auditor can see
 * what happened, and voiding asks for `purchasing.invoice`, the same capability that recorded it.
 * Paying it is not here: settlement lives under Finance → Payables, because an obligation that
 * exists is not the same as an obligation anyone has settled, and the two are different jobs held
 * by different people. What this page does owe payables is a clear refusal when a live payment
 * settles the invoice someone is trying to withdraw.
 */
export function SupplierInvoiceDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { can } = usePermissions();
  const { format } = useCurrency();

  const [isVoiding, setIsVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = usePurchaseInvoiceQuery(invoiceId);
  const voidInvoice = useVoidPurchaseInvoiceMutation();
  const settlingPayments = settlingReferences(voidInvoice.error);

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('purchasing.invoices.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const invoice: PurchaseInvoiceDetail = data;
  const isPosted = invoice.status === PurchaseInvoiceStatus.POSTED;
  // The invoice's own lifecycle: whoever may record one may correct one. Cancelling a purchase order is a different capability on a different document.
  const canVoid = isPosted && can(PERMISSIONS.PURCHASING_INVOICE);

  const confirmVoid = () => {
    if (!reason.trim()) return;
    voidInvoice.mutate(
      { id: invoice.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setFeedback(t('purchasing.invoices.voided', { reference: invoice.reference }));
          setIsVoiding(false);
          setReason('');
        },
      },
    );
  };

  const lineColumns: Column<PurchaseInvoiceLineSummary>[] = [
    {
      key: 'item',
      header: t('purchasing.orders.fields.item'),
      primary: true,
      render: (line) => (
        <span className="flex flex-col">
          <span className="text-ink text-sm" dir="auto">
            {line.variantName ?? line.productName}
          </span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {line.sku}
          </span>
        </span>
      ),
    },
    {
      key: 'source',
      header: t('purchasing.invoices.fields.source'),
      render: (line) => (
        <span className="flex flex-col font-mono text-xs" dir="ltr">
          <span className="text-ink">{line.receiptReference}</span>
          <span className="text-muted">{line.purchaseOrderReference}</span>
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('purchasing.invoices.fields.quantityInvoiced'),
      align: 'end',
      render: (line) => (
        <span className="text-ink" dir="ltr">
          {line.quantityInvoiced}
        </span>
      ),
    },
    {
      key: 'agreed',
      header: t('purchasing.orders.fields.agreedUnitCost'),
      align: 'end',
      render: (line) => (
        <span className="text-muted" dir="ltr">
          {format(line.agreedUnitCostMinor)}
        </span>
      ),
    },
    {
      key: 'billed',
      header: t('purchasing.invoices.fields.billedUnitCost'),
      align: 'end',
      render: (line) => (
        <span className="flex flex-col items-end">
          <span className="text-ink" dir="ltr">
            {format(line.billedUnitCostMinor)}
          </span>
          {line.unitCostVarianceMinor === 0 ? null : (
            <span className="text-muted text-xs" dir="ltr">
              {t('purchasing.invoices.varianceShort', {
                variance: format(Math.abs(line.unitCostVarianceMinor)),
                context: line.unitCostVarianceMinor > 0 ? 'above' : 'below',
              })}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'tax',
      header: t('purchasing.orders.fields.taxProfile'),
      render: (line) => (
        <span className="flex flex-col text-xs">
          <span className="text-ink">{t(`sales.taxTreatments.${line.taxTreatment}`)}</span>
          <span className="text-muted" dir="ltr">
            {t('purchasing.orders.fields.taxRate', { percent: line.taxRateBasisPoints / 100 })}
          </span>
        </span>
      ),
    },
    {
      key: 'total',
      header: t('purchasing.orders.fields.lineTotal'),
      align: 'end',
      render: (line) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(line.totalMinor)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={invoice.reference}
        subtitle={t('purchasing.invoices.detailSubtitle', {
          supplier: invoice.supplierName,
          number: invoice.supplierInvoiceNumber,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/purchasing/invoices')}>
              {t('purchasing.invoices.backToList')}
            </Button>
            {canVoid ? (
              <Button variant="danger" onClick={() => setIsVoiding(true)}>
                {t('purchasing.invoices.void')}
              </Button>
            ) : null}
          </div>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {invoice.status === PurchaseInvoiceStatus.VOIDED ? (
        <Alert tone="warning">
          {t('purchasing.invoices.voidedNotice', {
            reason: invoice.voidedReason ?? '',
            by: invoice.voidedByName ?? t('common.unknown'),
          })}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('purchasing.invoices.summarySection')}
          action={<PurchaseInvoiceStatusBadge status={invoice.status} />}
        />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail
              label={t('purchasing.invoices.fields.supplierInvoiceNumber')}
              value={invoice.supplierInvoiceNumber}
              ltr
            />
            <Detail
              label={t('purchasing.orders.fields.supplier')}
              value={invoice.supplierName}
            />
            <Detail label={t('purchasing.orders.fields.branch')} value={invoice.branchName} />
            <Detail
              label={t('purchasing.invoices.fields.invoiceDate')}
              value={invoice.invoiceDate}
              ltr
            />
            <Detail label={t('purchasing.invoices.fields.dueDate')} value={invoice.dueDate} ltr />
            <Detail
              label={t('purchasing.invoices.fields.paymentTerms')}
              value={t('purchasing.invoices.termsDays', { count: invoice.paymentTermsDays })}
            />
            <Detail
              label={t('purchasing.invoices.fields.postedBy')}
              value={invoice.postedByName ?? t('common.unknown')}
            />
            <Detail
              label={t('purchasing.invoices.fields.obligation')}
              value={format(invoice.obligationMinor)}
              ltr
            />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.invoices.linesSection')}
          subtitle={t('purchasing.invoices.lineCount', { count: invoice.lineCount })}
        />
        <DataTable
          caption={t('purchasing.invoices.linesCaption')}
          columns={lineColumns}
          rows={invoice.lines}
          rowKey={(line) => line.id}
          emptyTitle={t('purchasing.invoices.lineCount', { count: 0 })}
        />
        <CardBody className="space-y-2 text-sm">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.invoices.fields.net')}</span>
            <span className="text-ink" dir="ltr">
              {format(invoice.netMinor)}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('purchasing.invoices.fields.tax')}</span>
            <span className="text-ink" dir="ltr">
              {format(invoice.taxMinor)}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-ink font-medium">{t('purchasing.invoices.fields.total')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(invoice.totalMinor)}
            </span>
          </div>
        </CardBody>
      </Card>

      <Modal
        isOpen={isVoiding}
        onClose={() => setIsVoiding(false)}
        title={t('purchasing.invoices.voidTitle')}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsVoiding(false)}
              disabled={voidInvoice.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmVoid}
              isLoading={voidInvoice.isPending}
              disabled={!reason.trim() || voidInvoice.isPending}
            >
              {t('purchasing.invoices.void')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-ink text-sm">
            {t('purchasing.invoices.voidExplain', { reference: invoice.reference })}
          </p>
          <p className="text-muted text-sm">{t('purchasing.invoices.voidKeeps')}</p>
          {voidInvoice.isError ? (
            settlingPayments.length > 0 ? (
              // The invoice is paid. Nothing is cascaded here or on the server: the payment is a
              // separate document with its own lifecycle, and someone has to decide to withdraw it.
              <Alert tone="error">
                <span className="flex flex-col gap-1">
                  <span>{t('purchasing.invoices.settledBy')}</span>
                  <span className="font-mono font-medium" dir="ltr">
                    {settlingPayments.join(', ')}
                  </span>
                  <span>{t('purchasing.invoices.voidPaymentFirst')}</span>
                </span>
              </Alert>
            ) : (
              <Alert tone="error">
                {apiErrorMessage(voidInvoice.error) ?? t('purchasing.invoices.voidError')}
              </Alert>
            )
          ) : null}
          <Input
            label={t('purchasing.invoices.fields.voidReason')}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
